'use client';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { mappings, PermissionType } from '@/app/(default)/(leagues)/leagues/constants';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';

async function getUserPermissionsFromPB(moduleId: string) {
  const response = await fetch(`/api/v1/getUserPermissionsFromPB?moduleId=${moduleId}`);
  const responseData = await response.json();
  return responseData.data;
}

export const useGetUserPermissionsApp = (
  moduleId: string,
  viewerId: string | undefined,
  permissions: PermissionType[]
) => {
  const { hasPermission } = useAuth();
  const { session } = useSession();
  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const { data } = useQuery({
    queryKey: ['permissions', moduleId, viewerId],
    queryFn: () => getUserPermissionsFromPB(moduleId),
    refetchOnWindowFocus: false,
    // `moduleId` is '' until the league data has loaded; the route rejects an empty one.
    enabled: !!viewerId && !!moduleId,
  });
  const dataPermissions = data?.permissions.payload;

  const hasPermissions = useMemo(() => {
    if (session && session.user && isSuperAdmin) {
      return true;
    }
    if (!viewerId) {
      return false;
    }
    if (!data) {
      return null;
    }
    if (data && dataPermissions && dataPermissions?.length > 0) {
      const modulePermissions: PermissionType[] = dataPermissions[0]?.UserPermissions.split(',');

      if (modulePermissions.includes('IsPrimary')) {
        return true;
      }

      let mappedPermissions: PermissionType[] = [...permissions]; // this is check for alternate/mapped permissions
      permissions.forEach((item) => {
        const mapping = mappings[item];
        if (mapping) {
          mappedPermissions = [...mappedPermissions, ...mapping];
        }
      });

      const clearDuplicated = mappedPermissions.filter((item, index) => {
        return mappedPermissions.indexOf(item) === index;
      });

      return modulePermissions.some((permission) => clearDuplicated.includes(permission));
    }
    return false;
  }, [data, session, permissions]);

  return hasPermissions;
};
